import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListFlowsResult, type MqlSettingsResult, type NurtureResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Lead nurture enrolment (G-058), beside the MQL handoff it works below.
 *
 * The rejection reasons that send a lead to nurture are chosen, never
 * assumed: "works for a competitor" and "not this quarter" are both reasons
 * sales turns a lead back, and only one of them should get nurture email.
 *
 * The list underneath says who went in, how, and who left and why - so
 * "why is this lead still getting nurture email" has an answer on the screen.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function NurturePanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-069');
  const { can } = useSession();
  const allowed = enabled.enabled && can('segment.manage');

  const nurture = useQuery<NurtureResult, ApiError>({ queryKey: ['nurture'], queryFn: () => api.nurture({}), enabled: allowed });
  const journeys = useQuery<ListFlowsResult, ApiError>({
    queryKey: ['flows', 'journey'],
    queryFn: () => api.listFlows({ query: { palette: 'journey' } }),
    enabled: allowed,
  });
  const mql = useQuery<MqlSettingsResult, ApiError>({ queryKey: ['mql-settings'], queryFn: () => api.mqlSettings({}), enabled: allowed });

  const [draft, setDraft] = useState({ flowId: '', days: '', onRejection: false, reasons: [] as string[] });
  useEffect(() => {
    const saved = nurture.data?.settings;
    if (!saved) return;
    setDraft({
      flowId: saved.flowId ?? '',
      days: saved.enrolAfterDays === null ? '' : String(saved.enrolAfterDays),
      onRejection: saved.enrolOnRejection,
      reasons: saved.rejectionReasonIds,
    });
  }, [nurture.data]);

  const save = useMutation({
    mutationFn: () =>
      api.saveNurture({
        body: {
          flowId: draft.flowId || null,
          enrolAfterDays: draft.days.trim() === '' ? null : Number(draft.days),
          enrolOnRejection: draft.onRejection,
          rejectionReasonIds: draft.reasons,
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['nurture'] }),
  });

  if (!allowed) return null;
  if (nurture.isPending || journeys.isPending || mql.isPending) return null;
  if (nurture.isError) {
    return (
      <Panel data-region="nurture">
        <p className="text-dense text-danger-text">{errorText(nurture.error)}</p>
      </Panel>
    );
  }

  const reasons = (mql.data?.reasons ?? []).filter((reason) => reason.active);
  const flows = journeys.data?.flows ?? [];
  const enrolments = nurture.data.enrolments;

  return (
    <Panel data-region="nurture">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('nurture.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('nurture.help')}</p>

        <form
          data-nurture-form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('nurture.journey')}
            <select className={INPUT} data-nurture-journey value={draft.flowId}
              onChange={(event) => setDraft({ ...draft, flowId: event.target.value })}>
              <option value="">{t('nurture.off')}</option>
              {flows.map((flow) => (
                <option key={flow.flowId} value={flow.flowId}>{flow.name}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('nurture.days')}
            <input className={INPUT} type="number" min={1} max={365} data-nurture-days value={draft.days}
              placeholder={t('nurture.days.off')}
              onChange={(event) => setDraft({ ...draft, days: event.target.value })} />
          </label>

          <label className="flex items-center gap-2 text-dense text-ink">
            <input type="checkbox" data-nurture-on-rejection checked={draft.onRejection}
              onChange={(event) => setDraft({ ...draft, onRejection: event.target.checked })} />
            {t('nurture.onRejection')}
          </label>
          {draft.onRejection ? (
            <fieldset className="flex flex-col gap-1 ps-6" data-nurture-reasons>
              <legend className="text-meta text-ink-meta">{t('nurture.reasons')}</legend>
              {reasons.map((reason) => (
                <label key={reason.reasonId} className="flex items-center gap-2 text-dense text-ink-600">
                  <input type="checkbox" data-nurture-reason={reason.reasonId}
                    checked={draft.reasons.includes(reason.reasonId)}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        reasons: event.target.checked
                          ? [...draft.reasons, reason.reasonId]
                          : draft.reasons.filter((id) => id !== reason.reasonId),
                      })
                    } />
                  {reason.label}
                </label>
              ))}
              {draft.reasons.length === 0 ? (
                <p className="text-meta text-warning-text" data-nurture-no-reasons>{t('nurture.reasons.none')}</p>
              ) : null}
            </fieldset>
          ) : null}

          {save.error ? <p className="text-dense text-danger-text">{errorText(save.error as ApiError)}</p> : null}
          <div>
            <Button intent="primary" size="small" type="submit" data-nurture-save disabled={save.isPending}>
              {t('nurture.save')}
            </Button>
          </div>
        </form>

        {enrolments.length > 0 ? (
          <ul className="flex flex-col gap-1 text-meta" data-nurture-enrolments>
            {enrolments.slice(0, 20).map((entry) => (
              <li key={`${entry.leadId}-${entry.enrolledAt}`} data-nurture-enrolment={entry.leadId}
                className="flex flex-wrap gap-2 text-ink-600">
                <span className="text-ink">{entry.leadName || '—'}</span>
                <span>{t(`nurture.wayIn.${entry.wayIn}`)}</span>
                {entry.exitedAt ? (
                  <span className="text-ink-meta" data-nurture-left>{t('nurture.left', { values: { reason: entry.exitReason ?? '' } })}</span>
                ) : (
                  <span className="text-ink-meta">{t('nurture.inNow')}</span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}
